/**
 * ReservationsSearchSortSheet
 * Mobile bottom sheet for Reservations page: search input + sort options.
 * Used when user taps the search icon in the minimal sticky header.
 */

import { useEffect, useRef } from 'react';
import BottomSheet from './BottomSheet.jsx';

const SORT_OPTIONS = [
  { key: 'check_in_date', label: 'Check-in' },
  { key: 'check_out_date', label: 'Check-out' },
  { key: 'guest_name', label: 'Guest' },
  { key: 'channel', label: 'Channel' },
  { key: 'nights', label: 'Nights' },
  { key: 'revenue', label: 'Owner Net' },
];

export default function ReservationsSearchSortSheet({
  isOpen,
  onClose,
  searchQuery = '',
  onSearchChange,
  sortColumn = 'check_in_date',
  sortDirection = 'asc',
  onSortChange,
  resultCount = null,
}) {
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Search & sort"
      disableDrag={false}
    >
      <div style={styles.container}>
        <div style={styles.section}>
          <label htmlFor="reservations-sheet-search" style={styles.label}>
            Search reservations
          </label>
          <input
            ref={searchInputRef}
            id="reservations-sheet-search"
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="Guest, channel, dates..."
            aria-label="Search reservations"
            style={styles.searchInput}
          />
          {resultCount != null && searchQuery.trim() && (
            <p style={styles.resultCount}>
              {resultCount} result{resultCount !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>Sort by</h4>
          <div style={styles.sortList} role="listbox" aria-label="Sort options">
            {SORT_OPTIONS.map(({ key, label }) => {
              const isActive = sortColumn === key;
              const dir = isActive ? sortDirection : 'asc';
              return (
                <button
                  key={key}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  style={{
                    ...styles.sortOption,
                    ...(isActive ? styles.sortOptionActive : {}),
                  }}
                  onClick={() => onSortChange?.(key)}
                >
                  <span>{label}</span>
                  <span style={styles.sortIndicator}>
                    {dir === 'asc' ? '↑' : '↓'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          style={styles.doneButton}
          data-bottom-sheet-close
        >
          Done
        </button>
      </div>
    </BottomSheet>
  );
}

const styles = {
  container: {
    padding: 'var(--spacing-4)',
  },
  section: {
    marginBottom: 'var(--spacing-5)',
  },
  label: {
    display: 'block',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-2)',
  },
  searchInput: {
    width: '100%',
    padding: 'var(--spacing-2) var(--spacing-3)',
    fontSize: 'var(--font-size-base)',
    border: '1px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-bg-primary)',
    color: 'var(--color-text-primary)',
    boxSizing: 'border-box',
    minHeight: '44px',
  },
  resultCount: {
    margin: 'var(--spacing-2) 0 0',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
  },
  sectionTitle: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    marginBottom: 'var(--spacing-2)',
    marginTop: 0,
  },
  sortList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
  },
  sortOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: 'var(--spacing-2) var(--spacing-3)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-normal)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    minHeight: '44px',
    textAlign: 'left',
  },
  sortOptionActive: {
    borderColor: 'var(--color-brand-primary, #0f4c75)',
    backgroundColor: 'rgba(15, 76, 117, 0.06)',
    fontWeight: 'var(--font-weight-medium)',
  },
  sortIndicator: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
  },
  doneButton: {
    width: '100%',
    padding: 'var(--spacing-3)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-bg-primary)',
    backgroundColor: 'var(--color-brand-primary, #0f4c75)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    marginTop: 'var(--spacing-2)',
    minHeight: '44px',
  },
};
